import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { some } from 'lodash'
import routes from '../routes/routes'
import workOrderApi, {
  WorkOrderData,
  WorkOrderUpdateRequest
} from '../apis/workOrder.api'
import itemApi from '../apis/item.api'
import alertService from '../services/alert.service'
import confirmService from '../services/confirm.service'
import dateUtil from '../utils/date.util'
import ContentAside from '../components/ContentAside'
import ContentLayout from '../components/ContentLayout'
import ContentMain from '../components/ContentMain'
import WorkOrderEditForm, {
  WorkOrderEditFormData
} from '../partials/WorkOrderEditForm'
import ItemCard from '../components/ItemCard'
import { ConfirmParams } from '../components/Confirm/Confirm.state'
import ItemQuantityList from '../partials/ItemQuantityList'
import { i18n } from '../i18n/i18n'


interface WorkOrderEditRouteState {
  id: number
}

interface WorkOrderEditProps
  extends RouteComponentProps<any, any, WorkOrderEditRouteState> {}

interface WorkOrderEditState extends WorkOrderEditFormData {
  workOrder: WorkOrderData | null
}

class WorkOrderEdit extends Component<WorkOrderEditProps, WorkOrderEditState> {
  constructor (props: WorkOrderEditProps) {
    super(props)
    this.state = {
      itemId: '',
      line: '',
      salesOrder: '',
      quantity: '',
      adjustmentQuantity: '',
      productionDate: dateUtil.now(),
      workOrder: null
    }
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleDateChange = this.handleDateChange.bind(this)
    this.handleSaveClick = this.handleSaveClick.bind(this)
  }

  get id () {
    return this.props.location.state.id
  }

  updateWorkOrder () {
    const { line, salesOrder, quantity, adjustmentQuantity, productionDate } = this.state
    const payload: WorkOrderUpdateRequest = {}
    if (line) payload.lineNumber = line
    if (salesOrder) payload.salesOrder = salesOrder
    if (quantity) payload.quantity = Number(quantity)
    if (adjustmentQuantity) payload.adjustmentQuantity = Number(adjustmentQuantity)
    if (productionDate) payload.produceDate = productionDate
    workOrderApi.update(this.id, payload, workOrder => {
      if (!workOrder) return
      this.props.history.push(routes.workOrders)
    })
  }

  confirmMaterialDeficit () {
    const confirm: ConfirmParams = {
      id: 0,
      question: i18n('Please note this work order has a material deficit'),
      confirmText: i18n('Confirm'),
      cancelText: i18n('Cancel'),
      type: 'instruction',
      onConfirm: () => this.updateWorkOrder()
    }
    confirmService.push(confirm)
  }

  checkMaterialDeficit () {
    const { itemId, quantity } = this.state
    if (!itemId) return
    itemApi.materialsDelta(Number(itemId), Number(quantity), (materialDelta) => {
      const hasDeficit = some(materialDelta, data => data.delta < 0)
      if (hasDeficit) return this.confirmMaterialDeficit()
      this.updateWorkOrder()
    })
  }

  handleSaveClick () {
    const confirm: ConfirmParams = {
      id: 0,
      question: i18n('Please review and confirm the work order details'),
      confirmText: i18n('Confirm'),
      cancelText: i18n('Cancel'),
      onConfirm: () => this.checkMaterialDeficit()
    }
    confirmService.push(confirm)
  }

  get editForm () {
    const { workOrder } = this.state
    return workOrder &&
      <WorkOrderEditForm
        itemId={this.state.itemId}
        line={this.state.line}
        salesOrder={this.state.salesOrder}
        quantity={this.state.quantity}
        adjustmentQuantity={this.state.adjustmentQuantity}
        productionDate={this.state.productionDate}
        onInputChange={this.handleInputChange}
        onDateChange={this.handleDateChange}
        onSaveClick={this.handleSaveClick} />
  }

  get aside () {
    const { workOrder } = this.state
    if (!workOrder || !workOrder.item) return
    const quantity = Number(this.state.quantity) || 0
    return workOrder &&
      <div className='flex gap-5 flex-col'>
        <ItemCard
          code={workOrder.item.code}
          description={workOrder.item.description}
          cpp={workOrder.item.cpp}
          qpc={workOrder.item.qpc}
          workOrder={workOrder.code} />
        <ItemQuantityList
          heading='Production total calculation'
          item={workOrder.item}
          quantity={quantity} />
      </div>
  }

  handleInputChange (name: string, value: string) {
    if (name === 'line') this.setState({ line: value })
    else if (name === 'salesOrder') this.setState({ salesOrder: value })
    else if (name === 'quantity') this.setState({ quantity: value })
    else if (name === 'adjustmentQuantity') {
      this.setState({ adjustmentQuantity: value })
    }
  }

  handleDateChange (name: string, date: string) {
    if (name === 'productionDate') {
      this.setState({ productionDate: date.toString() })
    }
  }

  materialDeficitAlert () {
    const { workOrder } = this.state
    if (workOrder && workOrder.materialStockout) {
      const message = 'Please note this work order has a material deficit'
      alertService.instruction(message)
    }
  }

  readWorkOrder () {
    workOrderApi.read(this.id, workOrder => {
      if (
        !workOrder ||
        !workOrder.quantity ||
        !workOrder.item
      ) return
      const itemId = String(workOrder.itemId)
      const line = workOrder.line
      const salesOrder = workOrder.salesOrder || ''
      const quantity = String(workOrder.quantity.amount)
      const adjustmentQuantity = String(workOrder.quantity.adjustment)
      this.setState({
        itemId,
        line,
        salesOrder,
        quantity,
        adjustmentQuantity,
        workOrder
      }, () => {
        this.materialDeficitAlert()
      })
    })
  }

  componentDidMount () {
    this.readWorkOrder()
  }

  render () {
    return (
      <ContentLayout>
        <ContentMain layout="twoThird">
          {this.editForm}
        </ContentMain>
        <ContentAside layout="oneThird">
          {this.aside}
        </ContentAside>
      </ContentLayout>
    )
  }
}

export default withRouter(WorkOrderEdit)
